// src/pages/Budget/api.js
// API-слой АРМ «Доходы и расходы».
//
// Все «вычисления содержания» (проценты, невязки, семь балансовых проверок,
// признак неполноты периода, допуск тождеств) выполняет сервер и возвращает
// готовыми — компонент их не считает. Здесь сервер эмулируется на данных
// mockData: замените тело функций на реальные fetch/axios-вызовы, сохранив
// сигнатуры и форму ответов.
//
// Контракт ошибок повторяет модуль «Здравоохранение»: ApiError со status и body.
//   422 → body: { fields: { <field>: <message|ключ> }, step: <номер блока 1..4> }
//   409 → конфликт версий (запись изменена другим пользователем)

import {
  SECTIONS, BUDGETS, PERIODS, ARM_ROWS, NUMF, TOLERANCE,
  REF_SECTIONS, NO_EXEC_SECT, RES_SECTIONS, USE_SECTIONS,
} from './mockData';

export class ApiError extends Error {
  constructor(status, body) {
    super(`ApiError ${status}`);
    this.status = status;
    this.body = body || {};
  }
}

/* ------------------------------- утилиты --------------------------------- */
const DELAY = 220; // эмуляция сети (разбор файла в проде ≈ 0,2 c)
const wait = (v, ms = DELAY) => new Promise((res) => setTimeout(() => res(v), ms));
const has = (v) => !(v === null || v === undefined || v === '');
const z = (v) => (has(v) ? Number(v) : 0);
const isRef = (sec) => REF_SECTIONS.indexOf(sec) !== -1;

// Изменяемое хранилище строк периода (демо). id — суррогатный ключ.
let ROWS = ARM_ROWS.map((r, i) => ({ id: i + 1, budget: '8', date: '2026-05-31', ...r }));
let nextId = ROWS.length + 1;
let PERS = PERIODS.slice();

const codeStr = (r) => {
  if (r.code !== undefined && r.code !== null) return r.code;
  return [1, 2, 3, 4, 5, 6].map((i) => r[`code${i}`]).filter(has).join('.');
};
const codesArr = (r) => (codeStr(r) ? codeStr(r).split('.') : []);

// % — считает сервер, округляет один раз до 2 знаков; null при плане 0 / отсутствии.
const pct = (num, den) => (!has(num) || !has(den) || Number(den) === 0
  ? null
  : Number(((Number(num) / Number(den)) * 100).toFixed(2)));

/* --------------------------------- meta ---------------------------------- */
export function fetchBudgetMeta() {
  return wait({ sections: SECTIONS.slice(), budgets: BUDGETS.slice(), tolerance: TOLERANCE });
}

/* Список периodов выбранного бюджета (сервер создаёт периоды сам). */
export function fetchPeriods(budget) {
  const list = PERS.filter((p) => p.budget === budget)
    .map((p) => ({ date: p.date, is_partial: p.is_partial }))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
  return wait(list);
}

/* -------------------------------- перечень -------------------------------- */
// Серверная пагинация + фильтры. depth — число заполненных кодов (уровень).
export function fetchRows(params) {
  const { budget, period, section = '', depth = '', q = '', page = 1, size = 20 } = params;
  let list = ROWS.filter((r) => r.budget === budget && r.date === period);
  if (section) list = list.filter((r) => r.sec === section);
  if (depth !== '' && depth !== null) list = list.filter((r) => String(codesArr(r).length) === String(depth));
  if (q) {
    const s = String(q).toLowerCase();
    list = list.filter((r) => codeStr(r).toLowerCase().includes(s) || (r.name || '').toLowerCase().includes(s));
  }
  const total = list.length;
  const slice = list.slice((page - 1) * size, page * size).map((r) => ({
    id: r.id,
    sec: r.sec,
    code: codeStr(r),
    name: r.name,
    name_missing: r.name === '',
    level: codesArr(r).length,         // уровень для показа — считает сервер
    ub: r.ub, adj: r.adj, pp: r.pp, ex: r.ex,
    pct: pct(r.ex, r.pp),              // % к плану — считает сервер
  }));
  return wait({ rows: slice, total });
}

/* Полная запись для формы редактирования. */
export function fetchRow(id) {
  const r = ROWS.find((x) => x.id === id);
  if (!r) return Promise.reject(new ApiError(404, {}));
  const codes = codesArr(r);
  const out = {
    id: r.id, sec: r.sec, name: r.name,
    name_missing: r.name === '',
    version: r.version || 1,
  };
  for (let i = 1; i <= 6; i += 1) out[`code${i}`] = codes[i - 1] || '';
  NUMF.forEach((f) => { out[f] = r[f]; });
  return wait(out);
}

/* --------------------------- создание / правка ---------------------------- */
// Валидация здесь — «серверная» (дубли, родитель, версия). Клиентская
// (обязательность/формат) живёт в компоненте для скорости и её не заменяет.
function serverValidate(payload, editId) {
  const codes = [1, 2, 3, 4, 5, 6].map((i) => payload[`code${i}`]).filter(has);
  const code = codes.join('.');

  // Дубль: раздел + код в этом периоде и бюджете уже существует.
  const dup = ROWS.find((r) => r.id !== editId
    && r.budget === payload.budget && r.date === payload.date
    && r.sec === payload.sec && codeStr(r) === code);
  if (dup) throw new ApiError(422, { step: 1, fields: { codes: 'eDup' } });

  // Родительский узел должен существовать (кроме строки-итога раздела).
  if (codes.length > 1) {
    const parent = codes.slice(0, -1).join('.');
    const p = ROWS.find((r) => r.budget === payload.budget && r.date === payload.date
      && r.sec === payload.sec && codeStr(r) === parent);
    if (!p) throw new ApiError(422, { step: 1, fields: { codes: `eNoParent:${parent}` } });
  }
}

function toRecord(payload) {
  const codes = [1, 2, 3, 4, 5, 6].map((i) => payload[`code${i}`]).filter(has);
  const rec = {
    budget: payload.budget, date: payload.date, sec: payload.sec,
    code: codes.join('.'),
    name: payload.name_missing ? '' : (payload.name || ''),
  };
  // Для справочных разделов годовой бюджет и план не пишутся вовсе (NULL).
  NUMF.forEach((f) => {
    if (isRef(payload.sec) && ['ab', 'ub', 'adj', 'pp', 'cp', 'ca', 'ua'].includes(f)) {
      rec[f] = null;
    } else {
      rec[f] = has(payload[f]) ? Number(payload[f]) : null; // пусто → NULL, не 0
    }
  });
  return rec;
}

export function createRow(payload) {
  try {
    serverValidate(payload, null);
  } catch (e) {
    return wait(null).then(() => Promise.reject(e));
  }
  const rec = { id: nextId, version: 1, ...toRecord(payload) };
  const created = () => {
    nextId += 1;
    ROWS.push(rec);
    ensurePeriod(rec.budget, rec.date);
    return { row: rec, period_created: !periodExisted };
  };
  return wait(null).then(created);
}

let periodExisted = true;
function ensurePeriod(budget, date) {
  const p = PERS.find((x) => x.budget === budget && x.date === date);
  periodExisted = !!p;
  if (p) { p.rows += 1; } else {
    PERS.push({ id: PERS.length + 1, budget, date, is_partial: date.slice(5) !== '12-31', rows: 1 });
  }
}

export function updateRow(id, payload, version) {
  const cur = ROWS.find((r) => r.id === id);
  if (!cur) return wait(null).then(() => Promise.reject(new ApiError(404, {})));
  if (version !== undefined && cur.version && version !== cur.version) {
    return wait(null).then(() => Promise.reject(new ApiError(409, {}))); // конфликт версий
  }
  try {
    serverValidate({ ...payload, date: cur.date }, id);
  } catch (e) {
    return wait(null).then(() => Promise.reject(e));
  }
  Object.assign(cur, toRecord({ ...payload, budget: cur.budget, date: cur.date }));
  cur.version = (cur.version || 1) + 1;
  return wait({ row: cur, period_created: false });
}

/* ------------------------------- баланс ---------------------------------- */
// Сервер считает КPI, семь проверок и невязки. Компонент только рисует.
function secExec(budget, period, sec) {
  const r = ROWS.find((x) => x.budget === budget && x.date === period && x.sec === sec && !codeStr(x));
  return r && has(r.ex) ? Number(r.ex) : null;
}

export function fetchBalance(budget, period) {
  const V = (s) => z(secExec(budget, period, s));
  const inc = V('INC'), exp = V('EXP'), nbl = V('NBL'), fas = V('FAS'), def = V('DEF');
  const res = RES_SECTIONS.reduce((a, s) => a + V(s), 0);
  const use = USE_SECTIONS.reduce((a, s) => a + V(s), 0);

  const checks = [
    { id: 1, key: 'id1', formula: 'DEF = I − II − III − IV', residual: def - (inc - exp - nbl - fas) },
    { id: 2, key: 'id2', formula: 'VII = − V',               residual: V('FND') + def },
    { id: 3, key: 'id3', formula: 'III = БК − ПБК',          residual: nbl - (V('BC') - V('BCR')) },
    { id: 4, key: 'id4', formula: 'IV = ПФА − ППФА',         residual: fas - (V('FAP') - V('FAI')) },
    { id: 5, key: 'id5', formula: 'ИО = Онач − Окон',        residual: V('USB') - (V('BAL_B') - V('BAL_E')) },
    { id: 6, key: 'id6', formula: 'Ресурсы = Использование', residual: res - use },
    { id: 9, key: 'id9', formula: 'VII = ПЗ − ПогЗ + ИО',    residual: V('FND') - (V('LNI') - V('LNR') + V('USB')) },
  ].map((c) => ({ ...c, ok: Math.abs(c.residual) <= TOLERANCE }));

  const per = PERS.find((p) => p.budget === budget && p.date === period);
  return wait({
    budget, period,
    is_partial: per ? per.is_partial : period.slice(5) !== '12-31',
    tolerance: TOLERANCE,
    kpi: {
      resources: res,
      uses: use,
      residual: res - use,
      deficit: def,          // строка V, вводится, а не вычисляется
      is_surplus: def >= 0,
    },
    checks,
    missing_sections: null,  // напр. ['III','IV'] если введены не все разделы
    carryover: null,         // {ok, residual} если применимо
  });
}

/* --------------------------- загрузка файла ------------------------------ */
// Демо-ответ сервера. Реальный: POST /api/arm/budget/import (multipart),
// разбор эталонным парсером формы 1-27, замена периода одной транзакцией.
export function importFile({ budget, file }) {
  if (!file) return wait(null).then(() => Promise.reject(new ApiError(422, { code: 'noFile' })));

  // Демонстрируем ветки результата по расширению/имени файла:
  const name = String(file.name || '');
  const region = (BUDGETS.find((b) => b.k === budget) || {}).region;

  // Несовпадение региона (файл другого бюджета) — красная врезка.
  if (/budget3|бюджет3/i.test(name) && budget !== '3') {
    return wait({ ok: false, kind: 'region', file_region: BUDGETS[1].region, chosen_region: region });
  }
  // Ошибка разбора — красный список ошибок с номерами строк файла.
  if (/bad|ошиб/i.test(name)) {
    return wait({
      ok: false, kind: 'parse',
      errors: [
        { line: 842, message: 'раздел не найден в справочнике — «Временно привлечённые остатки»' },
        { line: 903, message: 'сумма не является числом — «—»' },
      ],
    });
  }

  const rows = 847;
  const resources = 74908651.54137;
  const violated = /viol|наруш/i.test(name);
  const checks = [1, 2, 3, 4, 5, 6, 9].map((id, i) => ({
    id,
    residual: violated && i === 5 ? 12.5 : 0,
    ok: !(violated && i === 5),
  }));

  return wait({
    ok: true,
    kind: violated ? 'violation' : 'success',
    file_name: name,
    rows,
    date: '2026-05-31',
    tolerance: TOLERANCE,
    kpi: { resources, uses: violated ? resources - 12.5 : resources, residual: violated ? 12.5 : 0 },
    checks,
  });
}
